import React, { memo, useCallback, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import { toast } from "react-toastify";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { AI_RISK_LEVEL_COLOR_BY_LABEL } from "../ai-risk-chart-constants";
import { PROVIDER_CONFORMITY_STATUS } from "src/modules/data-protection/constants";
import "./style.scss";

const resolveSoftwareId = (asset) =>
    asset?.softwareId ?? asset?.software_id ?? asset?.software?.id ?? null;

const buildAiRiskAssessmentPath = (softwareId) =>
    `/admin/data-protection/softwares/${softwareId}?tab=ai-risk-assessment`;

const complianceBadgeStyle = {
    "Non-Compliant": { backgroundColor: "#D32F2F", color: "#ffffff" },
    Monitoring: { backgroundColor: "#1976D2", color: "#ffffff" },
    Compliant: { backgroundColor: "#388E3C", color: "#ffffff" },
};

const SortCaret = () => (
    <i className="ri-arrow-down-s-line text-muted font-size-14 ms-1 align-middle" aria-hidden />
);

const resolveAnalystAssigned = (asset) =>
    asset?.analystAssigned ?? asset?.analyst_assigned ?? null;

const resolveProviderConformityRequired = (asset) =>
    asset?.providerConformityRequired ?? asset?.provider_conformity_required ?? null;

const resolveProviderConformityStatus = (asset) =>
    asset?.providerConformityStatus ?? asset?.provider_conformity_status ?? null;

const resolveConformityGapsCount = (asset) =>
    asset?.conformityGapsCount ?? asset?.conformity_gaps_count ?? 0;

const getAnalystDisplayName = (analyst) => {
    if (!analyst || Array.isArray(analyst)) {
        return null;
    }

    if (analyst.name) {
        return analyst.name;
    }

    const firstName = analyst.firstName ?? analyst.first_name ?? "";
    const lastName = analyst.lastName ?? analyst.last_name ?? "";
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || null;
};

const renderAssignedAnalyst = (analyst) => {
    const displayName = getAnalystDisplayName(analyst);

    if (!displayName) {
        return "-";
    }

    if (analyst.avatar) {
        return (
            <div className="d-flex gap-1 align-items-center">
                <div className="avatar-xs">
                    <span
                        className="avatar-title bg-soft-primary text-primary rounded-circle text-capitalize"
                        style={{ overflow: "hidden" }}
                    >
                        <img
                            alt={displayName}
                            src={analyst.avatar}
                            style={{
                                width: "100%",
                                height: "100%",
                                display: "block",
                                objectFit: "cover",
                            }}
                        />
                    </span>
                </div>
                <span>{displayName}</span>
            </div>
        );
    }

    return (
        <div>
            <span className="dt-list-col-bullet">{displayName[0]}</span>{" "}
            <span>{displayName}</span>
        </div>
    );
};

const DEFAULT_PAGE_SIZE = 5;
const AiAssetsOverviewTable = memo(({ t, lng, data = [], isFetching }) => {
    const onViewWithoutSoftware = useCallback(() => {
        toast(t("Unable to open software asset."), { type: "warning" });
    }, [t]);

    const tableData = useMemo(
        () =>
            (data || []).map((asset, index) => ({
                ...asset,
                rowKey:
                    asset.reportCaseId != null
                        ? String(asset.reportCaseId)
                        : `asset-${index}`,
            })),
        [data]
    );

    const columns = useMemo(
        () => [
            {
                dataField: "aiSystemName",
                text: (
                    <>
                        {t("AI System Name")}
                        <SortCaret />
                    </>
                ),
                formatter: (cell) => cell || "-",
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "provider",
                text: (
                    <>
                        {t("Provider")}
                        <SortCaret />
                    </>
                ),
                formatter: (cell) => cell || "-",
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "source",
                text: (
                    <>
                        {t("Source")}
                        <SortCaret />
                    </>
                ),
                formatter: (cell) => (cell === "Manual" ? t("Manual") : cell || "-"),
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "complianceStatus",
                text: (
                    <>
                        {t("Compliance Status")}
                        <SortCaret />
                    </>
                ),
                formatter: (cell) => {
                    const complianceStyle = complianceBadgeStyle[cell] || {
                        backgroundColor: "#6c757d",
                        color: "#ffffff",
                    };

                    return (
                        <span
                            className="rounded-pill d-inline-block px-3 py-1 small fw-semibold"
                            style={{
                                backgroundColor: complianceStyle.backgroundColor,
                                color: complianceStyle.color,
                            }}
                        >
                            {cell ? t(cell) : "-"}
                        </span>
                    );
                },
            },
            {
                dataField: "providerConformityRequired",
                text: (
                    <>
                        {t("Provider Conformity Required")}
                        <SortCaret />
                    </>
                ),
                formatter: (_cell, row) => {
                    const isRequired = resolveProviderConformityRequired(row);

                    if (isRequired == null) {
                        return "-";
                    }

                    return isRequired ? t("Yes") : t("No");
                },
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "providerConformityStatus",
                text: (
                    <>
                        {t("Provider Conformity Status")}
                        <SortCaret />
                    </>
                ),
                formatter: (_cell, row) => {
                    const statusKey = resolveProviderConformityStatus(row);

                    if (!statusKey) {
                        return "-";
                    }

                    const status = PROVIDER_CONFORMITY_STATUS[statusKey] || {
                        color: "secondary",
                        label: statusKey,
                    };

                    return (
                        <span
                            className={`dt-task-status bg-${status.color}`}
                            style={{
                                fontSize: "10px",
                                fontWeight: 700,
                                padding: "3px 8px",
                                color: "#1D1D1D",
                                borderRadius: "7px",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {t(status.label)}
                        </span>
                    );
                },
            },
            {
                dataField: "conformityGapsCount",
                text: (
                    <>
                        {t("Conformity Gaps")}
                        <SortCaret />
                    </>
                ),
                formatter: (_cell, row) => {
                    const gapsCount = Number(resolveConformityGapsCount(row)) || 0;

                    if (!resolveProviderConformityRequired(row)) {
                        return "-";
                    }

                    if (gapsCount > 0) {
                        return (
                            <span
                                className="rounded-pill d-inline-block px-3 py-1 small fw-semibold"
                                style={{
                                    backgroundColor: "#D32F2F",
                                    color: "#ffffff",
                                }}
                            >
                                {gapsCount}
                            </span>
                        );
                    }

                    return gapsCount;
                },
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "riskLevel",
                text: (
                    <>
                        {t("Risk Level")}
                        <SortCaret />
                    </>
                ),
                formatter: (cell) => {
                    const dotColor = AI_RISK_LEVEL_COLOR_BY_LABEL[cell] || "#6c757d";

                    return (
                        <span className="d-inline-flex align-items-center">
                            <span
                                className="d-inline-block rounded-circle flex-shrink-0 me-2"
                                style={{
                                    width: "10px",
                                    height: "10px",
                                    backgroundColor: dotColor,
                                }}
                                aria-hidden
                            />
                            <span>{cell ? t(cell) : "-"}</span>
                        </span>
                    );
                },
            },
            {
                dataField: "analystAssigned",
                text: (
                    <>
                        {t("Assigned Analyst")}
                        <SortCaret />
                    </>
                ),
                formatter: (_cell, row) =>
                    renderAssignedAnalyst(resolveAnalystAssigned(row)),
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "useCase",
                text: (
                    <>
                        {t("Use Case")}
                        <SortCaret />
                    </>
                ),
                formatter: (cell) => (cell ? t(cell) : "-"),
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "residualRisk",
                text: (
                    <>
                        {t("Residual Risk")}
                        <SortCaret />
                    </>
                ),
                formatter: (cell) => (cell ? t(cell) : "-"),
                style: { whiteSpace: "nowrap" },
            },
            {
                dataField: "actions",
                text: (
                    <span className="d-block text-center">
                        {t("Actions")}
                        <SortCaret />
                    </span>
                ),
                isDummyField: true,
                headerClasses: "text-center",
                classes: "text-center",
                formatter: (_cell, asset) => {
                    const softwareId = resolveSoftwareId(asset);
                    const viewPath = softwareId
                        ? buildAiRiskAssessmentPath(softwareId)
                        : null;

                    if (viewPath) {
                        return (
                            <Link
                                to={viewPath}
                                className="ai-assets-overview-action-btn"
                                title={t("View")}
                                aria-label={t("View")}
                            >
                                <i className="ri-eye-line font-size-18" />
                            </Link>
                        );
                    }

                    return (
                        <button
                            type="button"
                            className="ai-assets-overview-action-btn"
                            title={t("Unable to open software asset.")}
                            aria-label={t("Unable to open software asset.")}
                            onClick={onViewWithoutSoftware}
                        >
                            <i className="ri-eye-line font-size-18" />
                        </button>
                    );
                },
            },
        ],
        [t, lng, onViewWithoutSoftware]
    );

    const paginationOptions = useMemo(
        () => ({
            hideSizePerPage: false,
            hidePageListOnlyOnePage: false,
            sizePerPage: DEFAULT_PAGE_SIZE,
            sizePerPageList: [
                { text: "5", value: 5 },
                { text: "10", value: 10 },
                { text: t("All"), value: tableData.length || 1 },
            ],
        }),
        [tableData.length, t, lng]
    );

    if (isFetching) {
        return (
            <Card className="mb-3">
                <CardBody>
                    <h6 className="mb-3 fw-bold">{t("AI Assets Overview")}</h6>
                    <div className="dt-field dt-skeleton" style={{ width: "100%", height: "190px" }} />
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className="mb-3 card_box_Shadow">
            <CardBody>
                <Row>
                    <Col sm="12">
                        <h6 className="mb-3 fw-bold">{t("AI Assets Overview")}</h6>
                        <div
                            className="table-responsive ai-assets-overview-table-wrapper"
                            style={{ overflowX: "auto", maxWidth: "100%" }}
                        >
                            <BootstrapTable
                                key={lng}
                                keyField="rowKey"
                                data={tableData}
                                columns={columns}
                                pagination={paginationFactory(paginationOptions)}
                                noDataIndication={t("There is no data")}
                                bordered={false}
                                classes="align-middle mb-0 ai-assets-overview-table table-borderless"
                                headerWrapperClasses="ai-assets-overview-thead"
                            />
                        </div>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
});

export default withNamespaces()(AiAssetsOverviewTable);
